import { useState, useEffect, useMemo } from "react";
import { Briefcase, Building2, Calendar, MapPin, Clock, Euro, History } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { API } from "@/config";

const STATUS_META = {
  planned: { label: "Planifiée", cls: "bg-blue-100 text-blue-800", dot: "bg-blue-500" },
  in_progress: { label: "En cours", cls: "bg-[#F0E9F3] text-[#662483]", dot: "bg-[#662483]" },
  completed: { label: "Terminée", cls: "bg-green-100 text-green-800", dot: "bg-green-500" },
  cancelled: { label: "Annulée", cls: "bg-red-100 text-red-800", dot: "bg-red-500" },
};

function daysBetween(a, b) {
  if (!a) return 0;
  const s = new Date(a);
  const e = b ? new Date(b) : new Date();
  return Math.max(0, Math.round((e - s) / 86400000));
}

function humanDuration(d) {
  if (d < 30) return `${d} j`;
  const m = Math.floor(d / 30);
  if (m < 12) return `${m} mois`;
  const y = Math.floor(m / 12);
  const rm = m % 12;
  return `${y} an${y > 1 ? "s" : ""}${rm > 0 ? ` ${rm} mois` : ""}`;
}

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}

const TABS = [
  { key: "all", label: "Tout" },
  { key: "in_progress", label: "En cours" },
  { key: "planned", label: "À venir" },
  { key: "completed", label: "Historique" },
];

export default function MissionsScreen() {
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/me/missions`, { withCredentials: true })
      .then((r) => { if (!cancelled) setMissions(r.data || []); })
      .catch(() => { if (!cancelled) setMissions([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    if (tab === "all") return missions;
    return missions.filter((m) => m.status === tab);
  }, [missions, tab]);

  const totalDays = missions.reduce((sum, m) => sum + daysBetween(m.start_date, m.end_date), 0);

  return (
    <div className="p-4 space-y-4" data-testid="missions-screen">
      <div className="flex items-center gap-2">
        <History className="w-5 h-5 text-[#662483]" />
        <h1 className="text-lg font-bold text-[#3F3F3F]">Mes missions</h1>
        <Badge className="bg-[#F0E9F3] text-[#662483] rounded-none font-bold ml-1">{missions.length}</Badge>
        {totalDays > 0 && (
          <span className="text-[11px] text-gray-500 ml-auto">
            <Clock className="w-3 h-3 inline mr-0.5" />
            {humanDuration(totalDays)} cumulé
          </span>
        )}
      </div>

      {/* Tabs */}
      <div className="flex overflow-x-auto gap-1 -mx-1 pb-1" data-testid="missions-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 text-xs font-semibold whitespace-nowrap border transition-colors ${
              tab === t.key
                ? "bg-[#662483] text-white border-[#662483]"
                : "bg-white text-gray-600 border-gray-200 hover:border-[#662483]/40"
            }`}
            data-testid={`mission-tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading && <p className="text-xs text-gray-500 italic">Chargement…</p>}
      {!loading && filtered.length === 0 && (
        <div className="text-center py-8 text-sm text-gray-500 border border-dashed border-gray-200 bg-white" data-testid="missions-empty">
          <Briefcase className="w-8 h-8 mx-auto text-gray-300 mb-2" />
          Aucune mission {tab !== "all" && `(${TABS.find((t) => t.key === tab)?.label.toLowerCase()})`} pour le moment.
        </div>
      )}

      {/* Timeline */}
      {!loading && filtered.length > 0 && (
        <div className="relative pl-5" data-testid="missions-list">
          <div className="absolute left-1.5 top-2 bottom-2 w-px bg-gray-200" aria-hidden="true" />
          {filtered.map((m) => {
            const meta = STATUS_META[m.status] || STATUS_META.in_progress;
            const days = daysBetween(m.start_date, m.end_date);
            return (
              <div key={m.mission_id} className="relative pb-3" data-testid={`mission-card-${m.mission_id}`}>
                <span className={`absolute -left-4 top-2 w-3 h-3 rounded-full border-2 border-white shadow ${meta.dot}`} aria-hidden="true" />
                <div className="bg-white border border-gray-200 p-3">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="flex items-center gap-1.5 min-w-0 flex-1">
                      <Building2 className="w-3.5 h-3.5 text-[#662483] shrink-0" />
                      <span className="text-sm font-bold text-[#3F3F3F] truncate">{m.company_name}</span>
                    </div>
                    <Badge className={`rounded-none text-[10px] ${meta.cls} shrink-0`}>{meta.label}</Badge>
                  </div>
                  <p className="text-xs text-gray-700"><Briefcase className="w-3 h-3 inline mr-1 text-gray-400" />{m.job_title}</p>
                  <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-500 mt-2">
                    <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(m.start_date)}{m.end_date ? ` → ${formatDate(m.end_date)}` : " → en cours"}</span>
                    {days > 0 && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{humanDuration(days)}</span>}
                    {m.location && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{m.location}</span>}
                    {m.hourly_rate != null && <span className="flex items-center gap-1"><Euro className="w-3 h-3" />{m.hourly_rate}€/h</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
